import React from "react";
import tw from "twin.macro";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import LogoImage from "images/logo/ncc-logo.png";
import pMarkLogo from "images/logo/pmark_75.gif";
import FooterMobileView from "./FooterMobileView";

// Container and Content
const Container = tw.div`relative bg-[#0c4d96] text-gray-100 px-6 md:px-8 py-12 lg:py-16 rounded-lg`;
const Content = tw.div`max-w-screen-xl mx-auto`;

// Columns Layout with Gap
const ColumnsWrapper = tw.div`flex flex-wrap gap-y-8 lg:gap-y-0 gap-x-6 justify-between`;

// Individual Columns
const Column = tw.div`w-full text-left`; // Base width and left alignment
const Column4 = tw(Column)`lg:w-1/3`; // First column takes 4 out of 12 columns (1/3)
const Column8 = tw(Column)`lg:w-1/2`; // Second column takes 8 out of 12 columns (2/3)

// Logo and Address
const LogoContainer = tw.div`flex items-center justify-center md:justify-start`;
const PmarkLogoContainer = tw.div`flex bg-white items-center justify-center md:justify-start`;
const LogoImg = tw.img`w-32 mb-4`;
const LogoPrImg = tw.img`w-16 rounded-lg bg-white`;
const CompanyAddress = tw.p`text-sm leading-relaxed text-gray-300 mt-2 mb-0 max-w-xs mx-auto md:mx-0`;

// Section Headings
const ColumnHeading = tw.h5`font-bold uppercase text-base mb-4`; // Left-aligned

// Links
const LinkList = tw.ul`flex flex-wrap gap-4`; // Horizontal layout
const LinkListItem = tw.li`flex items-center justify-start`; // Left-aligned
const Link = tw.a`text-gray-300 hover:text-white transition duration-300 text-sm`;

// Divider and Footer Info
const Divider = tw.div`border-t-2 border-gray-500 my-6`;
const FooterBottomRow = tw.div`flex flex-col md:flex-row justify-between items-center text-sm text-gray-300`;

const Copyright = tw.div`text-center md:text-left mb-4 md:mb-0`;

export default ({ isXs = false }) => {
  const { t } = useTranslation();
  if (isXs) return <FooterMobileView />;
  return (
    <Container>
      <Content>
        {/* Top Section: Footer Columns */}
        <ColumnsWrapper>
          {/* Company Logo and Address - Column 4 */}
          <Column4>
            <LogoContainer>
              <LogoImg src={LogoImage} alt="Company Logo" />
            </LogoContainer>
            <CompanyAddress>
              {t("company_location.address.address_line1")}
              <br />
              {t("company_location.address.address_line2")}
              <br />
              {t("company_location.address.address_line3")}
            </CompanyAddress>
            {/* Contact Links */}
            <Box sx={{ mt: 2 }}>
              <CompanyAddress>{t("footer_conact_phone")}</CompanyAddress>
              <CompanyAddress style={{ whiteSpace: "nowrap" }}>
                {" "}
                <Link href={`mailto:${t("footer_contact_email_soumu")}`}>
                  {t("company_location.address.email_soumu")}
                </Link>
                <br />
                <Link href={`mailto:${t("footer_contact_email_sales")}`}>
                  {t("company_location.address.email_sales")}
                </Link>
              </CompanyAddress>
            </Box>
          </Column4>

          {/* Quick Links - Column 8 */}
          <Column8>
            <ColumnHeading>{t("footer_quick_link")}</ColumnHeading>
            <LinkList>
              <LinkListItem>
                <Link href="/it-service">{t("header_nav_service")}</Link>
              </LinkListItem>
              <LinkListItem>
                <Link href="/business">{t("header_nav_business")}</Link>
              </LinkListItem>
              <LinkListItem>
                <Link href="/company">{t("header_nav_aboutus")}</Link>
              </LinkListItem>
              <LinkListItem>
                <Link href="/recruitment">{t("header_nav_recruitment")}</Link>
              </LinkListItem>
              <LinkListItem>
                <Link href="/employee">{t("header_nav_employee")}</Link>
              </LinkListItem>
              <LinkListItem>
                <Link href="/contact">{t("header_nav_contactus")}</Link>
              </LinkListItem>
            </LinkList>

            <LinkList style={{ marginTop: "10px" }}>
              <LinkListItem>
                <Link href="/privacy-policy">
                  {t("header_nav_privacy_policy")}
                </Link>
              </LinkListItem>
            </LinkList>
          </Column8>
        </ColumnsWrapper>

        {/* Divider */}
        <Divider />

        {/* Bottom Row */}
        <FooterBottomRow>
          <Copyright>{t("footer_copyright")}</Copyright>
          <PmarkLogoContainer>
            <Link href="https://privacymark.jp/">
              <LogoPrImg src={pMarkLogo} alt="P Mark Logo" />
            </Link>
          </PmarkLogoContainer>
        </FooterBottomRow>
      </Content>
    </Container>
  );
};
